import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";

const mergeDocumentSelection = (current, incoming) => {
  const merged = [...current];
  const existingKeys = new Set(current.map((file) => `${file.name}|${file.size}|${file.lastModified}`));
  incoming.forEach((file) => {
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (!existingKeys.has(key) && merged.length < 10) {
      existingKeys.add(key);
      merged.push(file);
    }
  });
  return merged;
};
const MAX_DOCUMENT_SIZE = 2 * 1024 * 1024;

function UserManagement() {
  const [departments, setDepartments] = useState([]);

  const [users, setUsers] = useState([]); // User data
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const usersRequestId = useRef(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "", 
    oldPassword: "", // New field for old password
    auth: "",
    employeeID: "",
    newEmployeeID: "",
    department: "",
    status: "Active",
    isBlocked: "0",  
    whatsappNumber: "",
    logicAccountName: "",
    logicAccountCode: "",
    bankAccountNo: "",
    ifscCode: "",
    gender: "", aadhaarNumber: "", panNumber: "",
  });
  const [selectedUserId, setSelectedUserId] = useState(null); // Selected user for update/delete
  const [showModal, setShowModal] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]); // For row selection
  const [toggleClearSelectedRows, setToggleClearSelectedRows] = useState(false);
  const [searchText, setSearchText] = useState(""); // Search text
  const [documentsWorker, setDocumentsWorker] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [pendingFormDocuments, setPendingFormDocuments] = useState([]);

  // Get the user object from localStorage
  const user = JSON.parse(localStorage.getItem("user"));
  const isWorkerRole = String(formData.auth || "").trim().toLowerCase() === "user";

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    // Filter the users based on search text
    const filtered = users.filter(
      (user) =>
        (user.Name &&
          user.Name.toLowerCase().includes(searchText.toLowerCase())) ||
        (user.Auth &&
          user.Auth.toLowerCase().includes(searchText.toLowerCase())) ||
        (user.EmployeeID &&
          user.EmployeeID.toLowerCase().includes(searchText.toLowerCase())) ||
        (user.Department &&
          user.Department.toLowerCase().includes(searchText.toLowerCase()))
    );
    return filtered;
  }, [searchText, users]);

  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get("/api/departments");
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    const requestId = ++usersRequestId.current;
    setUsersLoading(true);
    setUsersError("");
    try {
      const response = await axiosInstance.get("/api/AllUsers");
      if (requestId !== usersRequestId.current) return;
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (requestId === usersRequestId.current) setUsersError("Unable to load users. Please try again.");
    } finally {
      if (requestId === usersRequestId.current) setUsersLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
      ...(name === "auth" && String(value).trim().toLowerCase() !== "user"
        ? {
            logicAccountName: "",
            logicAccountCode: "",
            bankAccountNo: "",
            ifscCode: "",
            gender: "", aadhaarNumber: "", panNumber: "",
          }
        : {}),
    }));
    if (name === "auth" && String(value).trim().toLowerCase() !== "user") {
      setPendingFormDocuments([]);
    }
  };

  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        <strong>${
          type === "success" ? "Success!" : "Error!"
        }</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 2000);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const transformedData = {
      Name: formData.name,
      Email: formData.email,     
      Password: formData.password, // Include password (may be empty)
      OldPassword: formData.oldPassword, // Include old password
      Auth: formData.auth,
      EmployeeID: formData.employeeID,
      NewEmployeeID: selectedUserId ? formData.newEmployeeID : undefined, // Only include newEmployeeID during updates
      Department: formData.department,
      Status: formData.status,
      IsBlocked: parseInt(formData.isBlocked, 10), 
      WhatsApp_Number: formData.whatsappNumber,
      LOGIC_ACCOUNT_NAME: formData.logicAccountName,
      LOGIC_ACCOUNT_CODE: formData.logicAccountCode,
      BANK_ACCOUNT_NO: formData.bankAccountNo,
      IFSC_CODE: formData.ifscCode,
      Gender: formData.gender,
      aadhaarNumber: formData.aadhaarNumber,
      panNumber: formData.panNumber,
    };

    try {
      let savedEmployeeId;
      if (selectedUserId) {
        // Update user
        transformedData.EmployeeID = formData.employeeID;
        transformedData.NewEmployeeID =
          formData.newEmployeeID || formData.employeeID;
        const response = await axiosInstance.put(
          `/api/users/${selectedUserId}`,
          transformedData
        );

        // Check for error response from backend
        if (response.data.error) {
          showAlert(response.data.error, "danger");
          return;
        } else {
          showAlert(response.data.message || "User updated successfully!", "success");
        }
        savedEmployeeId = transformedData.NewEmployeeID;
      } else {
        // Add new user
        const response = await axiosInstance.post("/api/signup", transformedData);
        showAlert(response.data.message || "User added successfully!", "success");
        savedEmployeeId = transformedData.EmployeeID;
      }

      if (String(formData.auth || "").trim().toLowerCase() === "user" && pendingFormDocuments.length) {
        const documentPayload = new FormData();
        pendingFormDocuments.forEach((file) => documentPayload.append("documents", file));
        try {
          await axiosInstance.post(
            `/api/worker-master/${encodeURIComponent(savedEmployeeId)}/documents`,
            documentPayload,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          await Swal.fire({ icon: "success", title: "User & Documents Saved", text: `${pendingFormDocuments.length} document(s) successfully upload ho gaye.`, confirmButtonColor: "#1769aa" });
        } catch (documentError) {
          fetchUsers();
          setShowModal(false);
          resetForm();
          await Swal.fire({ icon: "warning", title: "User Saved, Upload Failed", text: documentError.response?.data?.message || "User save ho gaya, lekin documents upload nahi hue. Update modal se dobara upload karein.", confirmButtonColor: "#1769aa" });
          return;
        }
      }

      fetchUsers();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error(
        "Error saving user:",
        error.response?.data || error.message
      );
      showAlert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "An error occurred while saving the user.",
        "danger"
      );
    }
  };

  const handleDeleteUser = async () => {
    if (selectedRows.length === 0) {
      showAlert("Please select a user to delete!", "danger");
      return;
    }

    // Get the name of the user being deleted
    const userToDelete = selectedRows[0].Name;

    // Show a confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this user: ${userToDelete}?`
    );

    if (!confirmDelete) {
      return; // Exit if the user cancels the action
    }

    try {
      // Assuming your backend can handle batch deletes; if not, loop through selectedRows
      await axiosInstance.delete(`/api/users/${selectedRows[0].EmployeeID}`);
      showAlert("User deleted successfully!", "success");
      fetchUsers();
      setSelectedRows([]);
      setToggleClearSelectedRows(!toggleClearSelectedRows);
    } catch (error) {
      console.error(
        "Error deleting user:",
        error.response?.data || error.message
      );
      showAlert("An error occurred while deleting the user.", "danger");
    }
  };

  const handleUpdateUser = () => {
    if (selectedRows.length === 0) {
      showAlert("Please select a user to update!", "danger");
      return;
    }

    if (selectedRows.length > 1) {
      showAlert("Select only one user to update!", "danger");
      return;
    }

    const userToEdit = selectedRows[0];
    setSelectedUserId(userToEdit.EmployeeID);
    setFormData({
      name: userToEdit.Name,
      email: userToEdit.Email || "",
      password: "",
      oldPassword:"", 
      auth: userToEdit.Auth,
      employeeID: userToEdit.EmployeeID,
      newEmployeeID: userToEdit.EmployeeID,
      department: userToEdit.Department,
      status: userToEdit.Status || "Active",
      isBlocked: userToEdit.IsBlocked ? "1" : "0",
      whatsappNumber: userToEdit.WhatsApp_Number || "",
      logicAccountName: userToEdit.LOGIC_ACCOUNT_NAME || "",
      logicAccountCode: userToEdit.LOGIC_ACCOUNT_CODE ?? "",
      bankAccountNo: userToEdit.BANK_ACCOUNT_NO || "",
      ifscCode: userToEdit.IFSC_CODE || "",
      gender: userToEdit.Gender || "",
      aadhaarNumber: userToEdit.AadhaarNumber || "",
      panNumber: userToEdit.PANNumber || "",
    });
    setPendingFormDocuments([]);
    setShowModal(true);
  };

  const handleAddUserClick = () => {
    resetForm(); // Clear the form data
    setShowModal(true); // Open the modal
  };
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      oldPassword: "",
      auth: "",
      employeeID: "",
      newEmployeeID: "",
      department: "",
      status: "Active",
      isBlocked: "0",
      whatsappNumber: "",
      logicAccountName: "",
      logicAccountCode: "",
      bankAccountNo: "",
      ifscCode: "",
      gender: "", aadhaarNumber: "", panNumber: "",
    });
    setPendingFormDocuments([]);
    setSelectedUserId(null);
    setSelectedRows([]);
    setToggleClearSelectedRows(!toggleClearSelectedRows);
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const canUploadWorkerDocuments = ["superadmin", "admin"].includes(String(user?.Auth || "").trim().toLowerCase());
  const formatFileSize = (size) => {
    const bytes = Number(size || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadWorkerDocuments = async (worker) => {
    try {
      setDocumentsLoading(true);
      const response = await axiosInstance.get(`/api/worker-master/${encodeURIComponent(worker.EmployeeID)}/documents`);
      setDocuments(response.data?.documents || []);
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Unable to Load", text: error.response?.data?.message || "Worker documents load nahi ho paye.", confirmButtonColor: "#1769aa" });
    } finally {
      setDocumentsLoading(false);
    }
  };

  const openWorkerDocuments = async (worker) => {
    if (!worker.EmployeeID || !worker.Name) {
      await Swal.fire({ icon: "warning", title: "Worker Details Required", text: "Documents open karne se pehle worker name aur Employee ID enter karein.", confirmButtonColor: "#1769aa" });
      return;
    }
    setDocumentsWorker(worker);
    setDocuments([]);
    setSelectedDocuments(worker.isDraft ? pendingFormDocuments : []);
    if (!worker.isDraft) await loadWorkerDocuments(worker);
  };

  const closeWorkerDocuments = () => {
    if (documentsUploading) return;
    setDocumentsWorker(null);
    setDocuments([]);
    setSelectedDocuments([]);
  };

  const selectWorkerDocuments = async (event) => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = "";
    const validFiles = incoming.filter((file) => file.size <= MAX_DOCUMENT_SIZE);
    const rejectedFiles = incoming.filter((file) => file.size > MAX_DOCUMENT_SIZE);
    setSelectedDocuments((current) => mergeDocumentSelection(current, validFiles));
    if (rejectedFiles.length) {
      await Swal.fire({ icon: "warning", title: "File Too Large", text: `${rejectedFiles.map((file) => file.name).join(", ")} upload nahi hoga. Har document maximum 2 MB hona chahiye.`, confirmButtonColor: "#1769aa" });
    }
  };

  const uploadWorkerDocuments = async () => {
    if (!selectedDocuments.length) {
      await Swal.fire({ icon: "warning", title: "Select Documents", text: "Upload ke liye document select karein.", confirmButtonColor: "#1769aa" });
      return;
    }
    const confirmation = await Swal.fire({
      icon: "question",
      title: documentsWorker.isDraft ? "Add selected documents?" : "Upload selected documents?",
      text: documentsWorker.isDraft
        ? `${selectedDocuments.length} document(s) will be added and uploaded when the user is saved. Do you want to continue?`
        : `${selectedDocuments.length} document(s) will be uploaded. Do you want to continue?`,
      showCancelButton: true,
      confirmButtonText: documentsWorker.isDraft ? "Yes, add" : "Yes, upload",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1769aa",
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;
    if (documentsWorker.isDraft) {
      const selectedCount = selectedDocuments.length;
      setPendingFormDocuments(selectedDocuments);
      setDocumentsWorker(null);
      setSelectedDocuments([]);
      await Swal.fire({ icon: "success", title: "Documents Added", text: `${selectedCount} document(s) will be uploaded when the user is saved.`, confirmButtonColor: "#1769aa" });
      return;
    }
    const payload = new FormData();
    selectedDocuments.forEach((file) => payload.append("documents", file));
    try {
      setDocumentsUploading(true);
      const response = await axiosInstance.post(`/api/worker-master/${encodeURIComponent(documentsWorker.EmployeeID)}/documents`, payload, { headers: { "Content-Type": "multipart/form-data" } });
      setSelectedDocuments([]);
      await loadWorkerDocuments(documentsWorker);
      await Swal.fire({ icon: "success", title: "Documents Uploaded", text: response.data?.message || "Documents successfully upload ho gaye.", confirmButtonColor: "#1769aa" });
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Upload Failed", text: error.response?.data?.message || "Documents upload nahi ho paye.", confirmButtonColor: "#1769aa" });
    } finally {
      setDocumentsUploading(false);
    }
  };

  // Define columns for DataTable
  const columns = [
    { name: "NAME", selector: (row) => row.Name, sortable: true },
    { name: "AUTH", selector: (row) => row.Auth, sortable: true },
    { name: "EMPLOYEE ID", selector: (row) => row.EmployeeID, sortable: true },
    { name: "DEPARTMENT", selector: (row) => row.Department, sortable: true },
  ];

  const handleSelectedRowsChange = (state) => {
    setSelectedRows(state.selectedRows);
  };


  return (
    <div className="d-flex dashboard">
      <div
        className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}
      >
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />
        <main className="main-container p-4"  style={{height:"-webkit-fill-available"}}>
          <div id="alertPlaceholder"></div>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-5">Manage Users</h2>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary mb-5 "
                onClick={handleAddUserClick}
              >
                Add New User
              </button>
              <button
                className="btn btn-outline-primary mb-5 "
                onClick={handleUpdateUser}
              >
                Update User
              </button>
              {/* <button
                className="btn btn-outline-danger mb-5"
                onClick={handleDeleteUser}
              >
                Delete User
              </button> */}
              {(user?.Auth === "SuperAdmin" || user?.Auth === "Admin") && ( // Show button only if Auth is "SuperAdmin"
                <button
                  className="btn btn-outline-danger mb-5"
                  onClick={handleDeleteUser}
                >
                  Delete User
                </button>
              )}
            </div>
          </div>

          {showModal && createPortal(
            <div 
              className="modal d-block" 
              style={{ backgroundColor: "rgba(0,0,0,0.5)", overflow: "hidden", padding: "16px", zIndex: 3000 }}
            >
              <div 
                className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
                style={{ width: "100%", maxWidth: "1100px", height: "calc(100vh - 32px)", margin: "0 auto" }}
              >
                <div className="modal-content shadow-lg border-0" style={{ height: "100%", maxHeight: "100%", overflow: "hidden" }}>
                  <div className="modal-header bg-primary text-white flex-shrink-0">
                    <h5 className="modal-title">
                      {selectedUserId ? "Update User" : "Add New User"}
                    </h5>
                    <button
                      className="btn-close btn-close-white"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                    <div className="modal-body" style={{ overflowY: "auto", minHeight: 0 }}>
                      <div className="row">
                        {/* --- LEFT COLUMN --- */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label fw-bold">Name <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="Enter Name"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-bold">
                              {selectedUserId ? "New Employee ID" : "Employee ID"} <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name={selectedUserId ? "newEmployeeID" : "employeeID"}
                              value={selectedUserId ? formData.newEmployeeID : formData.employeeID}
                              onChange={handleInputChange}
                              placeholder={selectedUserId ? "Enter New Employee ID" : "Enter Employee ID"}
                              inputMode={isWorkerRole ? "decimal" : undefined}
                              pattern={isWorkerRole ? "[0-9]+(?:[.][0-9]+)?" : undefined}
                              title={isWorkerRole ? "Worker Employee ID must be numeric" : undefined}
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-bold">WhatsApp Number</label>
                            <input
                              type="text"
                              className="form-control"
                              name="whatsappNumber"
                              value={formData.whatsappNumber || ""}
                              onChange={handleInputChange}
                              placeholder="e.g. +919XXXXXXXXX"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-bold">Department <span className="text-danger">*</span></label>
                            <select
                              className="form-control"
                              name="department"
                              value={formData.department}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Department</option>
                              <option value="None">None</option>
                              <option value="Reserve">Reserve worker</option>
                              <option value="Store">STORE</option>
                              <option value="IT Department">IT Department</option>
                              <option value="Accounts Department">Accounts Department</option>
                              <option value="Digital Marketing">Digital Marketing</option>
                              <option value="Production">Production</option>
                              <option value="Maintenance">Maintenance</option>
                              {departments.map((dept, index) => (
                                <option key={index} value={dept}>
                                  {dept}
                                </option>
                              ))}
                            </select>
                          </div>

                          {isWorkerRole && (
                            <>
                              <div className="mb-3">
                                <label className="form-label fw-bold">Logic Account Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="logicAccountName"
                                  value={formData.logicAccountName}
                                  onChange={handleInputChange}
                                  placeholder="Enter Logic Account Name"
                                  required
                                />
                              </div>
                              <div className="mb-3">
                                <label className="form-label fw-bold">Logic Account Code <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control"
                                  name="logicAccountCode"
                                  value={formData.logicAccountCode}
                                  onChange={handleInputChange}
                                  placeholder="Enter Logic Account Code"
                                  pattern="[0-9]+(?:[.][0-9]+)?"
                                  title="Logic Account Code must be numeric"
                                  required
                                />
                              </div>
                              <div className="mb-3">
                                <label className="form-label fw-bold" htmlFor="user-aadhaar">Aadhaar Card Number <span className="text-danger">*</span></label>
                                <input id="user-aadhaar" className="form-control" name="aadhaarNumber" value={formData.aadhaarNumber} onChange={handleInputChange} inputMode="numeric" pattern="[2-9][0-9]{11}" maxLength={12} title="Enter 12 digits starting with 2-9" placeholder="Enter 12-digit Aadhaar Number" required />
                              </div>
                              <div className="mb-3">
                                <label className="form-label fw-bold" htmlFor="user-pan">PAN Card Number <span className="text-danger">*</span></label>
                                <input id="user-pan" className="form-control text-uppercase" name="panNumber" value={formData.panNumber} onChange={handleInputChange} pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]" maxLength={10} title="Enter PAN in format ABCDE1234F" placeholder="e.g. ABCDE1234F" required />
                              </div>
                            </>
                          )}

                        </div>

                        {/* --- RIGHT COLUMN --- */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label fw-bold">Auth Role <span className="text-danger">*</span></label>
                            <select
                              className="form-control"
                              name="auth"
                              value={formData.auth}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Role</option>
                              <option value="Supervisor">Supervisor</option>
                              {user?.Auth === "SuperAdmin" && (
                                <>
                                  <option value="Admin">Admin</option>
                                  <option value="SuperAdmin">SuperAdmin</option>
                                  <option value="Staff">Staff</option>
                                </>
                              )}
                              <option value="User">Worker</option>
                            </select>
                          </div>

                          {/* MODERN RADIO BUTTONS FOR STATUS */}
                          <div className="mb-3">
                            <label className="form-label fw-bold d-block">Account Status</label>
                            <div className="btn-group w-100 shadow-sm" role="group">
                              <input type="radio" className="btn-check" name="status" id="statusActive" value="Active" checked={formData.status === "Active"} onChange={handleInputChange} />
                              <label className="btn btn-outline-success" htmlFor="statusActive">Active</label>

                              <input type="radio" className="btn-check" name="status" id="statusInactive" value="Inactive" checked={formData.status === "Inactive"} onChange={handleInputChange} />
                              <label className="btn btn-outline-warning" htmlFor="statusInactive">Inactive</label>

                              <input type="radio" className="btn-check" name="status" id="statusResigned" value="Resigned" checked={formData.status === "Resigned"} onChange={handleInputChange} />
                              <label className="btn btn-outline-danger" htmlFor="statusResigned">Resigned</label>
                            </div>
                          </div>

                          {/* MODERN RADIO BUTTONS FOR ACCESS (BLOCKED/UNBLOCKED) */}
                          {/* <div className="mb-3">
                            <label className="form-label fw-bold d-block">Account Access</label>
                            <div className="btn-group w-100 shadow-sm" role="group">
                              <input type="radio" className="btn-check" name="isBlocked" id="accessAllow" value="0" checked={String(formData.isBlocked) === "0" || formData.isBlocked === false} onChange={handleInputChange} />
                              <label className="btn btn-outline-success" htmlFor="accessAllow">Allowed</label>

                              <input type="radio" className="btn-check" name="isBlocked" id="accessBlock" value="1" checked={String(formData.isBlocked) === "1" || formData.isBlocked === true} onChange={handleInputChange} />
                              <label className="btn btn-outline-danger" htmlFor="accessBlock">Blocked</label>
                            </div>
                          </div>*/}

                          {selectedUserId ? (
                            <div className="row mb-3">
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Old Password</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="oldPassword"
                                  value={formData.oldPassword}
                                  onChange={handleInputChange}
                                  placeholder="Required to update password"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Password</label>
                                <input
                                  type="password"
                                  className="form-control"
                                  name="password"
                                  value={formData.password}
                                  onChange={handleInputChange}
                                  placeholder="Leave blank to keep same"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="mb-3">
                              <label className="form-label fw-bold">Password</label>
                              <input
                                type="password"
                                className="form-control"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter a new password"
                              />
                            </div>
                          )}

                          <div className="mb-3">
                            <label className="form-label fw-bold">Email (optional)</label>
                            <input
                              type="email"
                              className="form-control"
                              name="email"
                              value={formData.email || ""}
                              onChange={handleInputChange}
                              placeholder="Enter Email"
                            />
                          </div>

                          {isWorkerRole && (
                            <>
                              <div className="mb-3">
                                <label className="form-label fw-bold">Bank Account No. <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="bankAccountNo"
                                  value={formData.bankAccountNo}
                                  onChange={handleInputChange}
                                  placeholder="Enter Bank Account Number"
                                  required
                                />
                              </div>
                              <div className="mb-3">
                                <label className="form-label fw-bold">IFSC Code <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className="form-control text-uppercase"
                                  name="ifscCode"
                                  value={formData.ifscCode}
                                  onChange={handleInputChange}
                                  placeholder="e.g. HDFC0001234"
                                  pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}"
                                  title="Enter a valid 11-character IFSC code"
                                  required
                                />
                              </div>
                              <div className="mb-3">
                                <label className="form-label fw-bold">Gender <span className="text-danger">*</span></label>
                                <select
                                  className="form-control"
                                  name="gender"
                                  value={formData.gender}
                                  onChange={handleInputChange}
                                  required
                                >
                                  <option value="">Select Gender</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                            </>
                          )}

                          {String(formData.auth || "").trim().toLowerCase() === "user" && canUploadWorkerDocuments && (
                            <div className="mb-3">
                              <label className="form-label fw-bold">Worker Documents</label>
                              <button type="button" className="btn btn-outline-primary w-100" onClick={() => openWorkerDocuments({ EmployeeID: selectedUserId || formData.employeeID, Name: formData.name, isDraft: !selectedUserId })}>
                                <i className="bi bi-folder2-open me-2" />Add / View Documents{pendingFormDocuments.length ? ` (${pendingFormDocuments.length} selected)` : ""}
                              </button>
                              <div className="form-text">Documents manage karne ke liye button click karein.</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* MODAL FOOTER */}
                    <div className="modal-footer bg-light flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-secondary px-4 rounded-pill"
                        onClick={() => setShowModal(false)}
                      >
                        Close
                      </button>
                      <button type="submit" className="btn btn-primary px-4 rounded-pill shadow-sm">
                        {selectedUserId ? "Update User" : "Add User"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>,
            document.body
          )}

          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search Users..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: "auto" }}
            />
          </div>
          {usersError ? (
            <div className="alert alert-danger d-flex flex-wrap align-items-center justify-content-between gap-2" role="alert">
              <span>{usersError}</span>
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={fetchUsers}>Retry</button>
            </div>
          ) : <DataTable
            columns={columns}
            data={filteredUsers}
            progressPending={usersLoading}
            progressComponent={<div className="d-flex flex-column align-items-center justify-content-center gap-3 py-5" role="status" aria-live="polite" style={{ minHeight: 220 }}><span className="spinner-border text-primary" aria-hidden="true" /><span className="text-muted">Loading users, please wait…</span></div>}
            noDataComponent={<div className="py-5 text-muted">{searchText.trim() ? "No users match your search." : "No users found."}</div>}
            pagination
            selectableRows
            onSelectedRowsChange={handleSelectedRowsChange}
            clearSelectedRows={toggleClearSelectedRows}
          />}

          {documentsWorker && createPortal(
            <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3100 }} role="dialog" aria-modal="true">
              <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0">
                  <div className="modal-header bg-primary text-white">
                    <div><h5 className="modal-title"><i className="bi bi-folder2-open me-2" />Worker Documents</h5><small>{documentsWorker.Name} · ID {documentsWorker.EmployeeID}</small></div>
                    <button type="button" className="btn-close btn-close-white" onClick={closeWorkerDocuments} disabled={documentsUploading} />
                  </div>
                  <div className="modal-body bg-light">
                    {canUploadWorkerDocuments && (
                      <div className="card border-0 shadow-sm mb-4"><div className="card-body">
                        <h6><i className="bi bi-cloud-arrow-up me-2 text-primary" />Upload Documents</h6>
                        <p className="small text-muted">PDF, image, Word or Excel · maximum 10 files · 2 MB each</p>
                        <input type="file" multiple className="form-control mb-3" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" onChange={selectWorkerDocuments} disabled={documentsUploading} />
                        {selectedDocuments.length > 0 && <div className="d-flex flex-column gap-2 mb-3">
                          {selectedDocuments.map((file, index) => <div key={`${file.name}-${file.size}-${file.lastModified}`} className="d-flex align-items-center gap-2 border rounded bg-light px-3 py-2">
                            <i className="bi bi-file-earmark text-primary" /><span className="text-truncate flex-grow-1" title={file.name}>{file.name}</span><small className="text-muted text-nowrap">{formatFileSize(file.size)}</small>
                            <button type="button" className="btn btn-sm btn-link text-danger p-0 ms-1" onClick={() => setSelectedDocuments((current) => current.filter((_, fileIndex) => fileIndex !== index))} title="Remove selected file" aria-label={`Remove ${file.name}`}><i className="bi bi-x-circle-fill fs-5" /></button>
                          </div>)}
                          <div className="small text-muted">{selectedDocuments.length} file(s) selected</div>
                        </div>}
                        <button type="button" className="btn btn-primary" onClick={uploadWorkerDocuments} disabled={documentsUploading || !selectedDocuments.length}>
                          {documentsUploading ? <><span className="spinner-border spinner-border-sm me-2" />Uploading...</> : <><i className="bi bi-cloud-arrow-up me-2" />{documentsWorker.isDraft ? "Add to User" : "Upload Documents"}</>}
                        </button>
                      </div></div>
                    )}
                    {!documentsWorker.isDraft && <div className="d-flex justify-content-between mb-2"><strong>Uploaded Documents</strong><span className="text-muted small">{documents.length} file(s)</span></div>}
                    {!documentsWorker.isDraft && (documentsLoading ? <div className="text-center p-5"><div className="spinner-border text-primary" /></div> : documents.length === 0 ? (
                      <div className="text-center text-muted bg-white rounded p-5"><i className="bi bi-folder-x fs-2 d-block mb-2" />No documents uploaded</div>
                    ) : documents.map((document) => (
                      <a key={document.id} href={document.webUrl} target="_blank" rel="noreferrer" className="d-flex align-items-center gap-3 bg-white border rounded p-3 mb-2 text-decoration-none text-dark">
                        <i className="bi bi-file-earmark-text fs-4 text-primary" /><div className="flex-grow-1 overflow-hidden"><strong className="d-block text-truncate">{document.originalName || document.name.replace(/^\d+_\d+_/, "")}</strong><small className="text-muted">{formatFileSize(document.size)} · {new Date(document.lastModifiedDateTime).toLocaleString()}</small></div><i className="bi bi-box-arrow-up-right text-muted" />
                      </a>
                    )))}
                  </div>
                  <div className="modal-footer"><button type="button" className="btn btn-light" onClick={closeWorkerDocuments} disabled={documentsUploading}>Close</button></div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </main>
      </div>
    </div>
  );
}

export default UserManagement;
